import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Zap, Clock } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/ai-credits`;

const useStatus = () => {
  const { getAuthHeader } = useAuth();
  const [s, setS] = useState(null);
  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/status`, getAuthHeader());
      setS(r.data);
    } catch { /* additive, never block a page */ }
  }, [getAuthHeader]);
  useEffect(() => { load(); }, [load]);
  return s;
};

/** Sidebar balance pill, OFF by default. Only shown to people who asked to see their usage. */
export const AiCreditsPill = () => {
  const s = useStatus();
  if (!s || !s.show_usage) return null;
  const left = s.daily_limit != null ? Math.max(s.daily_limit - s.used_today, 0) : null;
  const hue = s.state === 'paused' ? '#f87171' : s.state === 'running_low' ? '#fbbf24' : '#22d3ee';
  return (
    <Link to="/app/ai-credits" data-testid="ai-credits-pill"
      className="flex items-center justify-between gap-2 w-full px-3 py-2 mb-2 rounded-lg border transition-colors duration-200 hover:bg-white/10"
      style={{ borderColor: `${hue}55`, background: `${hue}14` }}>
      <span className="flex items-center gap-2 text-xs text-slate-200">
        <Zap className="w-3.5 h-3.5" style={{ color: hue }} />AI today
      </span>
      <span className="text-xs font-semibold fp-num" style={{ color: hue }} data-testid="ai-credits-pill-value">
        {left != null ? `${left} left` : `${s.credit_balance} cr`}
      </span>
    </Link>
  );
};

/**
 * The only thing most users ever see: nothing at all until they're close to the limit,
 * then a plain message with when it unlocks and how to keep going.
 */
export const AiPauseNotice = ({ compact }) => {
  const s = useStatus();
  if (!s || s.state === 'ok') return null;
  const paused = s.state === 'paused';
  const hue = paused ? '#f87171' : s.state === 'on_credits' ? '#22d3ee' : '#fbbf24';

  return (
    <div className={`rounded-xl border ${compact ? 'p-3' : 'p-4'} mb-4`} data-testid="ai-pause-notice"
      style={{ borderColor: `${hue}66`, background: `${hue}14` }}>
      <div className="flex flex-wrap items-center gap-3">
        <Clock className="w-4 h-4 shrink-0" style={{ color: hue }} />
        <p className="text-sm flex-1 min-w-[240px]" style={{ color: '#e2e8f0' }}>{s.message}</p>
        {paused && (
          <div className="flex gap-2">
            <Link to="/app/ai-credits" data-testid="ai-pause-reload"
              className="fp-pill" style={{ borderColor: `${hue}88`, color: '#fff' }}>Add credits</Link>
            <Link to="/app/profile" data-testid="ai-pause-upgrade" className="fp-pill">Upgrade plan</Link>
          </div>
        )}
      </div>
      {paused && (
        <p className="text-[11px] text-slate-300 mt-2">
          Unlocks in about {s.hours_until_reset} hours. Everything the platform calculates, readiness, ROI,
          funding matches, your future paths, keeps working while AI is paused.
        </p>
      )}
    </div>
  );
};

/** Cost disclosure next to an AI button, also opt-in, for people who want to see it. */
export const AiCostHint = () => null;   // Retired: per-action credit costs stay internal.

export default AiCreditsPill;
